import {
  Component,
  OnInit,
  AfterViewInit,
  OnDestroy,
  ViewChild,
  inject,
  signal,
  computed,
  effect,
  untracked,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { finalize } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ApiService } from '../../../../core/services/api.service';
import { ProductConfigService } from '../../../../core/services/product-config.service';
import { CommonService } from '../../../../core/services/common.service';
import { LoaderService } from '../../../../core/services/loader.service';
import { AiBotService } from '../../../../core/services/ai-bot.service';
import {
  DatePickerComponent,
  DateRangePickerOutput,
  TimeUnit,
} from '../../../../shared/components/date-picker/date-picker.component';
import { IconComponentV2 } from '../../../../shared/components/icon-component/icon.component';
import {
  BarLineComponent,
  BarChartData,
  LegendData,
  ColorMap,
  TooltipConfig,
  LineConfig,
} from '../../../../shared/components/charts/bar-charts/bar-line/bar-line.component';
import {
  HorizontalBarChartComponent,
} from '../../../../shared/components/charts/horizontal-bar-chart/horizontal-bar-chart.component';
import {
  DonutChartComponent,
  ChartData,
} from '../../../../shared/components/charts/donut-chart/donut-chart.component';
import {
  PaginatedTableComponent,
  TableHeader,
} from '../../../../shared/components/tables/paginated-table/paginated-table.component';
import { IndividualToasterComponent } from '../../../../shared/components/loader/loader.component';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const LA_TIMEZONE = 'America/Los_Angeles';

/** KPI data shape */
interface TrainingKpis {
  completionRate: number;
  totalCompletedSessions: number;
  totalBookedSessions: number;
  totalNoShowSessions: number;
  totalCancelledSessions: number;
  totalNotInterestedSessions: number;
  noShowRate: number;
  cancelledRate: number;
  sessionWasteRate: number;
  postTrainingContractRate: number;
  wasteMoM: number | null;
  completionMoM: number | null;
  postContractMoM: number | null;
}

/** Decision card data shape */
interface TrainingDecision {
  capacityLeakRate: number;
  capacityLeakSessions: number;
  unregisteredPercent: number;
  unregisteredTotal: number;
  unregisteredUnconvertedCount: number;
  activationPercent: number;
  highIntentConvertedCount: number;
  distribution?: any[];
}

@Component({
  selector: 'app-training-conversion',
  standalone: true,
  imports: [
    CommonModule,
    DatePickerComponent,
    IconComponentV2,
    BarLineComponent,
    HorizontalBarChartComponent,
    DonutChartComponent,
    PaginatedTableComponent,
    IndividualToasterComponent,
  ],
  templateUrl: './training-conversion.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [],
})
export class TrainingConversionComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(DatePickerComponent) datePicker!: DatePickerComponent;
  @ViewChild(DonutChartComponent) donutChart!: DonutChartComponent;

  private apiService = inject(ApiService);
  private productConfigService = inject(ProductConfigService);
  private aiBotService = inject(AiBotService);
  public _commonService = inject(CommonService);
  private loaderService = inject(LoaderService);

  // ── Date/filter state ─────────────────────────────────────────────────────
  start_date = signal('2025-01-01');
  end_date = signal('2025-12-31');
  prev_start_date = signal('');
  prev_end_date = signal('');
  isPrevious = false;
  unit = signal<TimeUnit>('month');
  allowedGroupByUnits: any[] = ['day', 'week', 'month', 'quarter', 'year'];
  business_type = signal<string>('');
  selectedProductId = signal<string | null>(null);

  isVerificationLoading = signal<boolean>(false);
  verificationData = signal<{ count: number, percent: string, subtitle: string }>({ count: 0, percent: '0%', subtitle: '' });

  // ── Loading states ─────────────────────────────────────────────────────────
  isStatsLoading = signal(false);
  isPerformanceLoading = signal(false);
  isCompletionTypeLoading = signal(false);
  isTimeToContractLoading = signal(false);
  isUnregLoading = signal(false);
  isPostJourneyLoading = signal(false);
  isAdminTableLoading = signal(false);

  isDrillThroughOpen = signal(false);
  adminAvgConversion = signal<number>(0);

  get isFm(): boolean {
    return this.selectedProductId() === 'finance_mutual' || window.location.href.includes('finance_mutual');
  }

  toggleDrillThrough(): void {
    this.isDrillThroughOpen.update(v => !v);
  }

  isAnyLoading = computed(() =>
    this.isStatsLoading() ||
    this.isPerformanceLoading() ||
    this.isCompletionTypeLoading() ||
    this.isTimeToContractLoading() ||
    this.isUnregLoading() ||
    this.isPostJourneyLoading() ||
    this.isAdminTableLoading()
  );

  // ── KPI data ───────────────────────────────────────────────────────────────
  kpis = signal<TrainingKpis>({
    completionRate: 0,
    totalCompletedSessions: 0,
    totalBookedSessions: 0,
    totalNoShowSessions: 0,
    totalCancelledSessions: 0,
    totalNotInterestedSessions: 0,
    noShowRate: 0,
    cancelledRate: 0,
    sessionWasteRate: 0,
    postTrainingContractRate: 0,
    wasteMoM: null,
    completionMoM: null,
    postContractMoM: null,
  });

  decision = signal<TrainingDecision>({
    capacityLeakRate: 0,
    capacityLeakSessions: 0,
    unregisteredPercent: 0,
    unregisteredTotal: 0,
    unregisteredUnconvertedCount: 0,
    activationPercent: 0,
    highIntentConvertedCount: 0,
    distribution: [],
  });

  // ── Chart data ─────────────────────────────────────────────────────────────
  basePerformanceData: any[] = [];
  performanceData: BarChartData[] = [];
  performanceLines: LineConfig[] = [
    { key: 'completion', label: 'Completion Rate (%)', axis: 'right', color: '#0d9488' },
    { key: 'noshow', label: 'No-Show Rate (%)', axis: 'right', color: '#ef4444', type: 'dashed' },
  ];
  performanceLegend: any[] = [
    { label: 'Training Sessions', type: 'square', color: '#99d6cf', key: 'sessions', hidden: false },
    { label: 'Completion Rate (%)', type: 'solid-line', color: '#0d9488', key: 'completion', hidden: false },
    { label: 'No-Show Rate (%)', type: 'dashed-line', color: '#ef4444', key: 'noshow', hidden: false },
  ];
  performanceColors: ColorMap = {
    data1: '#99d6cf',
  };
  performanceTooltip: TooltipConfig[] = [
    { key: 'total', label: 'Training Sessions', type: 'number' },
    { key: 'completion', label: 'Completion Rate (%)', type: 'percent' },
    { key: 'noshow', label: 'No-Show Rate (%)', type: 'percent' },
  ];

  completionTypeData: { label: string; value: number; color: string }[] = [];
  timeToContractDonut: ChartData[] = [];

  adminTableData: any[] = [];
  readonly adminTableHeaders: TableHeader[] = [
    { label: 'ADMIN', key: 'admin', align: 'text-left', sortable: true, colClass: '!text-xs' },
    { label: 'HANDLED', key: 'unregHandled', align: 'text-left', sortable: true, colClass: '!text-xs' },
    { label: 'CONV. (BIZ)', key: 'convBusiness', align: 'text-left', sortable: true, colClass: '!text-xs' },
    { label: 'CONV. (PARTNER)', key: 'convPartner', align: 'text-left', sortable: true, colClass: '!text-xs' },
    { label: 'STILL UNREG.', key: 'stillUnreg', align: 'text-left', sortable: true, colClass: '!text-xs' },
    { label: 'CONV. RATE', key: 'convRate', align: 'text-left', sortable: true, isHtml: true, colClass: '!text-xs' },
    { label: 'TIER', key: 'tier', align: 'text-left', cellType: 'badge', colClass: '!text-xs' },
  ];

  private isInitialized = false;
  private isInitialDateRangeLoaded = false;

  readonly DONUT_COLORS = ['#059669', '#2563eb', '#ea580c', '#e11d48', '#0d9488'];

  constructor() {
    effect(() => {
      const globalProductId = this.apiService.selectedProductId();
      if (globalProductId) {
        untracked(() => {
          this.selectedProductId.set(globalProductId);
        });
      }
    });


  }

  ngOnInit(): void { }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.datePicker) {
        const preset = this.datePicker.predefinedRanges.find(p => p.label === 'Last 90 Days');
        if (preset) {
          this.datePicker.selectPreset(preset);
        } else {
          this.fetchAllData();
        }
      } else {
        this.fetchAllData();
      }
      this.isInitialized = true;
    }, 0);
  }

  ngOnDestroy(): void { }

  onDateRangeChange(event: DateRangePickerOutput): void {
    if (!this.isInitialDateRangeLoaded) {
      this.isInitialDateRangeLoaded = true;
      return;
    }

    const startStr = this.formatApiDate(event.start_date);
    const endStr = this.formatApiDate(event.end_date);
    this.start_date.set(startStr);
    this.end_date.set(endStr);

    let prevStart = event.previous_start_date ? this.formatApiDate(event.previous_start_date) : '';
    let prevEnd = event.previous_end_date ? this.formatApiDate(event.previous_end_date) : '';

    if (!prevStart || !prevEnd) {
      const diffDays = dayjs(endStr).diff(dayjs(startStr), 'day') + 1;
      prevStart = dayjs(startStr).subtract(diffDays, 'day').format('YYYY-MM-DD');
      prevEnd = dayjs(endStr).subtract(diffDays, 'day').format('YYYY-MM-DD');
    }

    this.isPrevious = true;
    this.prev_start_date.set(prevStart);
    this.prev_end_date.set(prevEnd);

    if (event.preset_label === 'Today' || event.preset_label === 'Yesterday' || this.start_date() === this.end_date()) {
      this.allowedGroupByUnits = ['hour', 'day', 'week', 'month', 'quarter', 'year'];
    } else {
      this.allowedGroupByUnits = ['day', 'week', 'month', 'quarter', 'year'];
    }

    this.unit.set(event.unit);
    this.fetchAllData();
  }

  openAiAssistant(): void {
    this._commonService.isPanelOpen.set(true);
    const productId = this.selectedProductId() || this.apiService.selectedProductId();
    if (!productId) return;
    const productName = this.productConfigService.getProductName(productId).toLowerCase();
    this.aiBotService.resolveAgent(productName, 'training_analytics_all', 'ask_prism_ai').subscribe({
      next: (res: any) => {
        const agentId = res?.agent_id || res?.data?.agent_id;
        this._commonService.activeAgentId.set(agentId || null);
      },
      error: () => this._commonService.activeAgentId.set(null),
    });
  }

  /** Recover estimate: 10% of no-shows × 6 contracts per session (configurable) */
  get recoverableContracts(): number {
    return Math.round(this.kpis().totalNoShowSessions * 0.1 * 6);
  }

  /** Activation 32% → target 43% */
  get activationSprintUpsideContracts(): number {
    return Math.round(this.kpis().totalBookedSessions * 0.13 * 0.25);
  }

  formatPercent(value: number | null | undefined, decimals = 1): string {
    if (value === null || value === undefined) return '—';
    return `${(value ?? 0).toFixed(decimals)}%`;
  }

  formatNumber(value: number): string {
    return new Intl.NumberFormat('en-US').format(Math.round(value ?? 0));
  }

  formatMoM(value: number | null): string {
    if (value === null) return '0.0%';
    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
  }

  momClass(value: number | null, invert: boolean = false): string {
    if (value === null) return '';
    if (invert) return value >= 0 ? 'text-red-500' : 'text-emerald-500';
    return value >= 0 ? 'text-emerald-500' : 'text-red-500';
  }

  momDotClass(value: number | null, invert: boolean = false): string {
    if (value === null) return 'bg-slate-400';
    if (invert) return value >= 0 ? 'bg-red-500' : 'bg-emerald-500';
    return value >= 0 ? 'bg-emerald-500' : 'bg-red-500';
  }

  tierClass(tier: string): string {
    const t = (tier || '').toLowerCase();
    if (t.includes('top') || t.includes('good')) return 'bg-emerald-50 text-emerald-600 border border-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-400';
    if (t.includes('average') && !t.includes('below')) return 'bg-amber-50 text-amber-600 border border-amber-100 dark:bg-amber-500/10 dark:text-amber-400';
    if (t.includes('below') || t.includes('poor')) return 'bg-red-50 text-red-600 border border-red-100 dark:bg-red-500/10 dark:text-red-400';
    return 'bg-slate-50 text-slate-600 border border-slate-100 dark:bg-slate-500/10 dark:text-slate-400';
  }

  // ── Blocking Activation Data ────────────────────────────────────────────────
  isBlockingLoading = signal(false);
  blockingActivationData: any[] = [];
  blockingActivationSubtitle: string = '';

  // ── Private API helpers ───────────────────────────────────────────────────

  fetchAllData(): void {
    this.fetchStats();
    this.fetchPerformanceOverview();
    this.fetchCompletionByType();
    this.fetchTimeToFirstContract();
    this.fetchUnregisteredGap();
    this.fetchPostRegistrationJourney();
    this.fetchAdminTable();
    this.fetchVerificationStatus();
    if (this.isFm) {
      this.fetchBlockingActivation();
    }
  }

  private fetchVerificationStatus(): void {
    this.isVerificationLoading.set(true);
    this.postApi('/business_verification_status')
      .pipe(finalize(() => this.isVerificationLoading.set(false)))
      .subscribe({
        next: (res: any) => {
          const data = res?.data || res;
          let count = 0;
          let percent = '0%';
          let subtitle = '';

          if (data) {
            if (data.verified_businesses != null) count = Number(data.verified_businesses);
            else if (data.verified_customers != null) count = Number(data.verified_customers);
            else if (data.count != null) count = Number(data.count);
            else if (data.total != null) count = Number(data.total);

            if (data.verified_percent != null) percent = `${data.verified_percent}%`;
            else if (data.percent != null) percent = `${data.percent}%`;

            if (data.note != null) subtitle = data.note;
          }

          if (Array.isArray(data)) {
            const row = data.find((r: any) => String(r.status || r.name).toLowerCase().includes('verified'));
            if (row) {
              count = Number(row.count ?? row.total ?? 0);
              if (row.percent) percent = `${row.percent}%`;
            }
          }

          this.verificationData.set({ count, percent, subtitle });
        }
      });
  }

  private fetchBlockingActivation(): void {
    this.isBlockingLoading.set(true);
    forkJoin({
      base: this.postApi('/customer_base_overall'),
    })
      .pipe(finalize(() => this.isBlockingLoading.set(false)))
      .subscribe({
        next: (res: any) => {
          const data = res.base?.data || res.base;
          if (!data) return;

          const noTraining = data.no_training || 0;
          const noBankAccount = data.no_bank_account || 0;
          const transfersDisabled = data.transfers_disabled || 0;
          const notApproved = data.not_approved || 0;

          this.blockingActivationData = [
            { label: 'No Training', value: noTraining, color: '#d32f2f' },
            { label: 'No Bank Account', value: noBankAccount, color: '#f59e0b' },
            { label: 'Transfers Disabled', value: transfersDisabled, color: '#0ea5e9' },
            { label: 'Not Approved', value: notApproved, color: '#64748b' }
          ].sort((a, b) => b.value - a.value);

          this.blockingActivationSubtitle = `${this.formatNumber(noTraining)} businesses with no training is the single biggest blocker · then ${this.formatNumber(noBankAccount)} without a bank account`;
        }
      });
  }

  private postApi(endpoint: string, isPrev = false) {
    const formatDate = (d: string) => {
      if (!d) return d;
      if (d.length <= 10) return d;
      return dayjs.utc(d).tz(LA_TIMEZONE).format('YYYY-MM-DD');
    };

    const productId = this.apiService.selectedProductId() || 'denefits';
    const productConfig = this.productConfigService.getProductConfig(
      this.productConfigService.getProductName(productId)
    );
    const toolsPath = productConfig.reportToolsPath || 'visionboard/tools';

    const isFm = productId === 'finance_mutual' || window.location.href.includes('finance_mutual');

    const startValue = formatDate(isPrev ? this.prev_start_date() : this.start_date());
    const endValue = formatDate(isPrev ? this.prev_end_date() : this.end_date());

    const payload: any = {
      unit: this.unit(),
      type: this.business_type(),
    };

    if (isFm) {
      payload.start_date = startValue;
      payload.end_date = endValue;
    } else {
      payload.start_time = startValue;
      payload.end_time = endValue;
    }

    return this.apiService.post(
      `${toolsPath}${endpoint}`,
      payload,
      {
        addAccessToken: productConfig.requiresAccessToken,
        addSecurityHeaders: false,
        withCredentials: false,
        productServerName: productConfig.productServerName,
        showLoader: false,
        showErrorToaster: false,
      }
    ).pipe(catchError(() => of(null)));
  }

  private fetchStats(): void {
    this.isStatsLoading.set(true);
    const productId = this.apiService.selectedProductId() || 'denefits';
    const isFm = productId === 'finance_mutual' || window.location.href.includes('finance_mutual');
    const endpoint = isFm ? '/stats' : '/training/stats';
    const calls: any = { current: this.postApi(endpoint) };
    if (this.isPrevious) {
      calls.previous = this.postApi(endpoint, true);
    }
    forkJoin(calls)
      .pipe(finalize(() => this.isStatsLoading.set(false)))
      .subscribe({
        next: (res: any) => {
          const d = res.current?.data || {};
          const prevD = res.previous?.data || {};

          const completionRate = parseFloat(String(d.completion_rate_percent ?? '0').replace('%', ''));
          const noShowRate = parseFloat(String(d.no_show_rate_percent ?? '0').replace('%', ''));
          const cancelledRate = parseFloat(String(d.cancelled_rate_percent ?? '0').replace('%', ''));
          const sessionWasteRate = noShowRate + cancelledRate;

          let completionMoM = null;
          let wasteMoM = null;

          if (this.isPrevious && res.previous) {
            const prevNoShowRate = parseFloat(String(prevD.no_show_rate_percent ?? '0').replace('%', ''));
            const prevCancelledRate = parseFloat(String(prevD.cancelled_rate_percent ?? '0').replace('%', ''));
            const prevSessionWasteRate = prevNoShowRate + prevCancelledRate;

            const prevCompletionRate = parseFloat(String(prevD.completion_rate_percent ?? '0').replace('%', ''));

            if (isFm) {
              completionMoM = prevCompletionRate > 0 ? ((completionRate - prevCompletionRate) / prevCompletionRate) * 100 : (completionRate > 0 ? 100 : 0);
              wasteMoM = prevSessionWasteRate > 0 ? ((sessionWasteRate - prevSessionWasteRate) / prevSessionWasteRate) * 100 : (sessionWasteRate > 0 ? 100 : 0);
            } else {
              completionMoM = completionRate - prevCompletionRate;
              wasteMoM = sessionWasteRate - prevSessionWasteRate;
            }
          }

          this.kpis.update(k => ({
            ...k,
            completionRate,
            noShowRate,
            cancelledRate,
            sessionWasteRate,
            totalCompletedSessions: Number(d.total_completed_sessions ?? 0),
            totalBookedSessions: Number(d.total_booked_sessions ?? 0),
            totalNoShowSessions: Number(d.total_no_show_sessions ?? 0),
            totalCancelledSessions: Number(d.total_cancelled_sessions ?? 0),
            totalNotInterestedSessions: Number(d.total_not_interested_sessions ?? 0),
            completionMoM,
            wasteMoM,
          }));
          this.decision.update(dec => ({
            ...dec,
            capacityLeakRate: noShowRate,
            capacityLeakSessions: Number(d.total_no_show_sessions ?? 0),
          }));
        },
      });
  }

  private fetchPerformanceOverview(): void {
    const isFm = this.selectedProductId() === 'finance_mutual' || window.location.href.includes('finance_mutual');
    const endpoint = isFm ? '/training_by_period' : '/training/performance_overview';

    this.isPerformanceLoading.set(true);
    this.postApi(endpoint)
      .pipe(finalize(() => this.isPerformanceLoading.set(false)))
      .subscribe({
        next: (res: any) => {
          if (isFm) {
            const payload = res?.data || res || {};
            const monthsData = payload.months || [];
            this.basePerformanceData = monthsData.map((item: any) => ({
              unit_date: item.unit_date || '',
              total: Number(item.sessions_held ?? 0),
              completion: parseFloat(item.completion_rate ?? '0'),
              noshow: parseFloat(item.no_show_rate ?? '0'),
            }));
          } else {
            this.basePerformanceData = (res?.data ?? []).map((item: any) => ({
              unit_date: item.unit_date || item.start || '',
              total: Number(item.total_booked_sessions ?? 0),
              completion: parseFloat(item.completion_rate_percent ?? '0'),
              noshow: parseFloat(item.no_show_rate_percent ?? '0'),
            }));
          }
          this.updatePerformanceChart();
        },
      });
  }

  private fetchCompletionByType(): void {
    if (this.isFm) return;

    this.isCompletionTypeLoading.set(true);
    this.postApi('/training/completion_by_type')
      .pipe(finalize(() => this.isCompletionTypeLoading.set(false)))
      .subscribe({
        next: (res: any) => {
          this.completionTypeData = (res?.data ?? [])
            .filter((i: any) => i.training_type)
            .sort((a: any, b: any) => Number(b.completion_percent) - Number(a.completion_percent))
            .map((item: any, idx: number) => {
              const fullLabel = String(item.training_type).replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());
              let shortLabel = fullLabel;
              if (shortLabel.length > 20) {
                shortLabel = shortLabel.substring(0, 17) + '...';
              }
              return {
                label: shortLabel,
                tooltipLabel: fullLabel,
                value: Number(item.completion_percent ?? 0),
                color: '#0d9488',
              };
            }).sort((a: any, b: any) => b.value - a.value);
        },
      });
  }

  private fetchTimeToFirstContract(): void {
    const isFm = this.selectedProductId() === 'finance_mutual' || window.location.href.includes('finance_mutual');
    const endpoint = isFm ? '/time_to_first_contract_distribution' : '/training/time_to_first_contract_distribution';

    this.isTimeToContractLoading.set(true);
    this.postApi(endpoint)
      .pipe(finalize(() => this.isTimeToContractLoading.set(false)))
      .subscribe({
        next: (res: any) => {
          let raw: any[] = [];
          let distribution: any[] = [];

          if (isFm) {
            const rawData = res?.data || res || {};
            raw = Array.isArray(rawData) ? rawData : (rawData?.distribution || []);
            distribution = rawData?.distribution || [];
          } else {
            raw = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
          }

          // Re-bucket: 0-7, 8-14, 15-30, 30+  (combine 31-60+61-90+90+)
          const buckets: { [key: string]: { label: string; users: number; pct: number } } = {
            '0-7': { label: '0–7 Days', users: 0, pct: 0 },
            '8-14': { label: '8–14 Days', users: 0, pct: 0 },
            '15-30': { label: '15–30 Days', users: 0, pct: 0 },
            '30+': { label: '30+ Days', users: 0, pct: 0 },
          };
          raw.forEach((item: any) => {
            const bucket = String(item.time_to_first_contract_bucket || item.bucket || '');
            const pct = Number(item.percent_of_total ?? item.percent_share ?? 0);
            const users = Number(item.users_count ?? 0);
            if (bucket.startsWith('0-7')) { buckets['0-7'].users += users; buckets['0-7'].pct += pct; }
            else if (bucket.startsWith('8-14') || bucket === '8-30 days') { buckets['8-14'].users += users; buckets['8-14'].pct += pct; }
            else if (bucket.startsWith('15-30')) { buckets['15-30'].users += users; buckets['15-30'].pct += pct; }
            else { buckets['30+'].users += users; buckets['30+'].pct += pct; }
          });

          const totalPct = Object.values(buckets).reduce((sum, b) => sum + b.pct, 0);

          if (totalPct > 0) {
            this.timeToContractDonut = Object.values(buckets).map((b, idx) => ({
              label: b.label,
              value: b.pct,
              count: b.users,
              color: this.DONUT_COLORS[idx % this.DONUT_COLORS.length],
            }));

            if (this.donutChart) {
              this.donutChart.legendShape = 'square';
            }
          } else {
            this.timeToContractDonut = [];
          }

          // Activation sprint: percentage for "within 7 days" only
          const activationPct = buckets['0-7'].pct;
          this.decision.update(dec => ({
            ...dec,
            activationPercent: activationPct,
            distribution: distribution
          }));

          // Target Window Cumulative Calculation (2 weeks: 0-7 and 8-14)
          const targetWindowCount = buckets['0-7'].users + buckets['8-14'].users;
          const targetWindowPct = buckets['0-7'].pct + buckets['8-14'].pct;
          this.verificationData.set({
            count: targetWindowCount,
            percent: targetWindowPct.toFixed(2) + '%',
            subtitle: ''
          });
        },
      });
  }

  private fetchUnregisteredGap(): void {
    const isFm = this.selectedProductId() === 'finance_mutual' || window.location.href.includes('finance_mutual');
    const endpoint = isFm ? '/unregistered_booking_conversion' : '/training/unregistered_booking_conversion';

    this.isUnregLoading.set(true);
    this.postApi(endpoint)
      .pipe(finalize(() => this.isUnregLoading.set(false)))
      .subscribe({
        next: (res: any) => {
          const rows: any[] = res?.data ?? [];
          const total = rows.reduce((s: number, r: any) => s + Number(r.users_count ?? 0), 0);
          const stillUnreg = rows.find((r: any) =>
            String(r.registration_status || '').toLowerCase().includes('still unregistered') ||
            String(r.registration_status || '').toLowerCase().includes('unregistered')
          );
          const pct = stillUnreg ? Number(stillUnreg.percent_share ?? 0) : 0;
          const unconvertedCount = stillUnreg ? Number(stillUnreg.users_count ?? 0) : 0;
          this.decision.update(dec => ({
            ...dec,
            unregisteredPercent: pct,
            unregisteredTotal: total,
            unregisteredUnconvertedCount: unconvertedCount,
          }));
        },
      });
  }

  private fetchPostRegistrationJourney(): void {
    const isFm = this.selectedProductId() === 'finance_mutual' || window.location.href.includes('finance_mutual');
    const endpoint = isFm ? '/post_registration_journey_contract' : '/training/post_registration_journey_contract';

    this.isPostJourneyLoading.set(true);
    const calls: any = { current: this.postApi(endpoint) };
    if (this.isPrevious) {
      calls.previous = this.postApi(endpoint, true);
    }
    forkJoin(calls)
      .pipe(finalize(() => this.isPostJourneyLoading.set(false)))
      .subscribe({
        next: (res: any) => {
          let rows: any[] = [];
          if (isFm) {
            const currentData = res.current?.data || res.current || [];
            rows = Array.isArray(currentData) ? currentData : [];
          } else {
            rows = res.current?.data ?? [];
          }

          const madeTotal = rows
            .filter((r: any) => {
              const status = String(r.contract_status || '').toLowerCase();
              if (isFm) {
                return status.includes('made') || status.includes('created') || status === 'contract after training';
              }
              return status.includes('made');
            })
            .reduce((s: number, r: any) => s + Number(r.users_count ?? r.user_count ?? 0), 0);
          const grandTotal = rows.reduce((s: number, r: any) => s + Number(r.users_count ?? r.user_count ?? 0), 0);
          const rate = grandTotal > 0 ? (madeTotal / grandTotal) * 100 : 0;

          let postContractMoM = null;
          if (this.isPrevious && res.previous) {
            let prevRows: any[] = [];
            if (isFm) {
              const prevData = res.previous?.data || res.previous || [];
              prevRows = Array.isArray(prevData) ? prevData : [];
            } else {
              prevRows = res.previous?.data ?? [];
            }

            const prevMadeTotal = prevRows
              .filter((r: any) => {
                const status = String(r.contract_status || '').toLowerCase();
                if (isFm) {
                  return status.includes('made') || status.includes('created') || status === 'contract after training';
                }
                return status.includes('made');
              })
              .reduce((s: number, r: any) => s + Number(r.users_count ?? r.user_count ?? 0), 0);
            const prevGrandTotal = prevRows.reduce((s: number, r: any) => s + Number(r.users_count ?? r.user_count ?? 0), 0);
            const prevRate = prevGrandTotal > 0 ? (prevMadeTotal / prevGrandTotal) * 100 : 0;
            if (isFm) {
              postContractMoM = prevRate > 0 ? ((rate - prevRate) / prevRate) * 100 : (rate > 0 ? 100 : 0);
            } else {
              postContractMoM = rate - prevRate;
            }
          }

          this.kpis.update(k => ({ ...k, postTrainingContractRate: rate, postContractMoM }));
        },
      });
  }

  private fetchAdminTable(): void {
    const isFm = this.selectedProductId() === 'finance_mutual' || window.location.href.includes('finance_mutual');
    const endpoint = isFm ? '/admins_conversion' : '/training/full_admin_conversion_table';

    this.isAdminTableLoading.set(true);
    this.postApi(endpoint)
      .pipe(finalize(() => this.isAdminTableLoading.set(false)))
      .subscribe({
        next: (res: any) => {
          let sum = 0;
          const dataArr = isFm ? (res?.admins ?? res?.data?.admins ?? []) : (res?.data ?? []);

          this.adminTableData = dataArr.map((item: any) => {
            sum += parseFloat(item.conversion_rate || '0');
            return {
              admin: item.admin_name || 'Unassigned',
              unregHandled: Number(item.total_unreg_handled ?? item.handled ?? 0),
              convBusiness: Number(item.conv_doctor ?? item.conv_business ?? item.conv_biz ?? 0),
              convPartner: Number(item.conv_partner ?? 0),
              stillUnreg: Number(item.still_unregistered ?? 0),
              convRate: `<span class="text-purple-600 dark:text-purple-400 font-medium">${item.conversion_rate ?? 0}%</span>`,
              tier: item.tier || 'N/A',
              tier_class: this.tierClass(item.tier),
            };
          });

          const avg = this.adminTableData.length > 0 ? sum / this.adminTableData.length : 0;
          this.adminAvgConversion.set(avg);

          // Post-training contract rate fallback: avg conversion rate from admin table
          if (this.kpis().postTrainingContractRate === 0 && this.adminTableData.length) {
            this.kpis.update(k => ({ ...k, postTrainingContractRate: avg }));
          }
          // Count high-intent conversions (non-still-unreg)
          const converted = this.adminTableData.reduce(
            (s: number, r: any) => s + r.convBusiness + r.convPartner, 0
          );
          this.decision.update(dec => ({ ...dec, highIntentConvertedCount: converted }));
        },
      });
  }

  private formatApiDate(d: string): string {
    if (!d) return '';
    const iso = d.match(/^\d{4}-\d{2}-\d{2}/)?.[0];
    if (iso) return iso;
    const parsed = new Date(d);
    return Number.isNaN(parsed.getTime()) ? d : parsed.toISOString().slice(0, 10);
  }

  onLegendClick(item: any): void {
    const legendItem = this.performanceLegend.find(l => l.key === item.key);
    if (legendItem) {
      legendItem.hidden = !legendItem.hidden;
      this.performanceLegend = [...this.performanceLegend];
      this.updatePerformanceChart();
    }
  }

  private updatePerformanceChart(): void {
    const showSessions = !this.performanceLegend.find(l => l.key === 'sessions')?.hidden;
    const showCompletion = !this.performanceLegend.find(l => l.key === 'completion')?.hidden;
    const showNoShow = !this.performanceLegend.find(l => l.key === 'noshow')?.hidden;

    this.performanceLines = ([
      { key: 'completion', label: 'Completion Rate (%)', axis: 'right', color: '#0d9488' },
      { key: 'noshow', label: 'No-Show Rate (%)', axis: 'right', color: '#ef4444', type: 'dashed' },
    ] as LineConfig[]).filter(l => (l.key === 'completion' && showCompletion) || (l.key === 'noshow' && showNoShow));

    this.performanceData = this.basePerformanceData.map(d => ({
      ...d,
      total: showSessions ? d.total : 0
    }));

    this.performanceTooltip = [
      { key: 'total', label: 'Training Sessions', type: 'number' },
      { key: 'completion', label: 'Completion Rate (%)', type: 'percent' },
      { key: 'noshow', label: 'No-Show Rate (%)', type: 'percent' },
    ].filter(l =>
      (l.key === 'total' && showSessions) ||
      (l.key === 'completion' && showCompletion) ||
      (l.key === 'noshow' && showNoShow)
    );
  }
}
